// Domain maps — these mirror models/constants.go and models/ltrAudit.go in
// api-ltr-service. Keep the two in step: the BE sends ids, the FE owns the
// labels and colours.

export const STATUS_NEW_LEAD = 1;
export const STATUS_IN_PROGRESS = 2;
export const STATUS_CLOSED = 3;

export const STATUS_LABEL_MAP = {
  [STATUS_NEW_LEAD]: "New lead",
  [STATUS_IN_PROGRESS]: "In progress",
  [STATUS_CLOSED]: "Closed",
};

export const STATUS_CLASS_MAP = {
  [STATUS_NEW_LEAD]: "new-lead",
  [STATUS_IN_PROGRESS]: "in-progress",
  [STATUS_CLOSED]: "closed",
};

export const STATUS_OPTIONS = [
  { value: STATUS_NEW_LEAD, label: "New lead" },
  { value: STATUS_IN_PROGRESS, label: "In progress" },
  { value: STATUS_CLOSED, label: "Closed" },
];

export const OUTCOME_SUCCESSFUL = 1;
export const OUTCOME_UNSUCCESSFUL = 2;
export const OUTCOME_FALSE_POSITIVE = 3;
export const OUTCOME_USER_NOT_INTERESTED = 4;

export const OUTCOME_LABEL_MAP = {
  [OUTCOME_SUCCESSFUL]: "Closed successfully",
  [OUTCOME_UNSUCCESSFUL]: "Closed unsuccessfully",
  [OUTCOME_FALSE_POSITIVE]: "False positive",
  [OUTCOME_USER_NOT_INTERESTED]: "User not interested",
};

// A closed case is always a green pill. The outcome is the mark *inside* that
// pill rather than a different pill colour, so the queue reads "closed" at a
// glance and the outcome is the detail.
export const OUTCOME_ICON_MAP = {
  [OUTCOME_SUCCESSFUL]: "check_circle",
  [OUTCOME_UNSUCCESSFUL]: "cancel",
  [OUTCOME_FALSE_POSITIVE]: "flag",
  [OUTCOME_USER_NOT_INTERESTED]: "block",
};

export const OUTCOME_OPTIONS = [
  { value: OUTCOME_SUCCESSFUL, label: "Closed successfully" },
  { value: OUTCOME_UNSUCCESSFUL, label: "Closed unsuccessfully" },
  { value: OUTCOME_FALSE_POSITIVE, label: "False positive" },
  { value: OUTCOME_USER_NOT_INTERESTED, label: "User not interested" },
];

// A case can carry several actions at once, so `actions` is an array on the
// wire. Labels are worded as the Assign action popup words them; the short
// form is the dashboard's Action pill.
export const ACTION_OPTIONS = [
  { value: 1, label: "Conduct diagnostic test", short: "Diagnostic test" },
  { value: 2, label: "Schedule follow-up consultation", short: "Follow-up consult" },
  { value: 3, label: "Refer to super specialist consultation", short: "Specialist referral" },
  { value: 4, label: "Prescribe medicine", short: "Prescribe medicine" },
  { value: 5, label: "Suggest visiting hospital", short: "Hospital visit" },
  { value: 6, label: "Vital follow-up", short: "Vital follow-up" },
];

export const ACTION_LABEL_MAP = ACTION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

export const ACTION_SHORT_LABEL_MAP = ACTION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.short;
  return acc;
}, {});

// Contact consent, read off the patient's appointments when the case was
// opened (consent 2 follow-up / 4 health advice in the last two years). Only a
// case with consent is routed to a doctor at intake; one without waits,
// unassigned and with no follow-up date, for someone to take it by hand.
// consentGiven is a boolean on the wire; these ids exist for the filter popover.
export const CONSENT_YES = 1;
export const CONSENT_NO = 0;

export const CONSENT_OPTIONS = [
  { value: CONSENT_YES, label: "Yes" },
  { value: CONSENT_NO, label: "No" },
];

// The account the LTR programme plan lives under. The plan id itself differs
// per environment, so it is read off the case (planId), never hard-coded.
export const LTR_DC_ACCOUNT_CODE = "01XXDC";

// Who a case is assigned to. Exactly one person holds a case at a time.
export const ROLE_DOCTOR = "doctor";
export const ROLE_COORDINATOR = "coordinator";

export const ROLE_LABEL_MAP = {
  [ROLE_DOCTOR]: "Doctor",
  [ROLE_COORDINATOR]: "Coordinator",
};

// Doctor's evaluation of the AI summary as the LTR case stores it. Note the
// numbering differs from the risk service (3 correct / 4 incorrect).
export const LTR_EVALUATION_CORRECT = 2;
export const LTR_EVALUATION_INCORRECT = 3;

// Activity log event types.
export const AUDIT_LEAD_CREATED = "lead_created";
export const AUDIT_CASE_ASSIGNED = "case_assigned";
export const AUDIT_CASE_HANDED_OVER = "case_handed_over";
export const AUDIT_ACTION_SET = "action_set";
export const AUDIT_STATUS_CHANGED = "status_changed";
export const AUDIT_CALL_LOGGED = "call_logged";
export const AUDIT_FOLLOW_UP_CHANGED = "follow_up_changed";
export const AUDIT_CASE_CLOSED = "case_closed";
export const AUDIT_SUMMARY_EVALUATED = "ai_summary_evaluated";
export const AUDIT_APPOINTMENT_CREATED = "appointment_created";
export const AUDIT_PRESCRIPTION_LINKED = "prescription_linked";
export const AUDIT_PLAN_ASSIGNED = "plan_assigned";
export const AUDIT_SUMMARY_GENERATED = "ai_summary_generated";
// A further assessment of the patient, folded into their open case.
export const AUDIT_ASSESSMENT_ADDED = "assessment_added";

// The activity log's Type column and the Where it came through. Milestone
// rows are the case's status timeline.
export const AUDIT_TYPE_MAP = {
  [AUDIT_LEAD_CREATED]: { type: "Action", source: "Prism · automated" },
  [AUDIT_CASE_ASSIGNED]: { type: "Task", source: "Bridge · case handover" },
  [AUDIT_CASE_HANDED_OVER]: { type: "Task", source: "Bridge · task assignment" },
  [AUDIT_ACTION_SET]: { type: "Action", source: "Bridge · LTR case detail" },
  [AUDIT_STATUS_CHANGED]: { type: "Milestone", source: "Bridge · LTR case detail" },
  [AUDIT_CALL_LOGGED]: { type: "Call", source: "Bridge · outbound call" },
  [AUDIT_FOLLOW_UP_CHANGED]: { type: "Update", source: "Bridge · LTR case detail" },
  [AUDIT_CASE_CLOSED]: { type: "Milestone", source: "Bridge · LTR case detail" },
  [AUDIT_SUMMARY_EVALUATED]: { type: "Action", source: "Bridge · AI summary" },
  [AUDIT_APPOINTMENT_CREATED]: { type: "Action", source: "Bridge · outbound call" },
  [AUDIT_PRESCRIPTION_LINKED]: { type: "Action", source: "Bridge · prescription" },
  [AUDIT_PLAN_ASSIGNED]: { type: "Action", source: "Prism · automated" },
  [AUDIT_SUMMARY_GENERATED]: { type: "Update", source: "Prism · automated" },
  [AUDIT_ASSESSMENT_ADDED]: { type: "Update", source: "Prism · automated" },
};

// Follow-up window presets, as the Assign action and Update follow-up
// popups offer them.
export const FOLLOW_UP_PRESETS = [
  { hours: 24, label: "24 hours" },
  { hours: 48, label: "2 days" },
  { hours: 72, label: "3 days" },
  { hours: 120, label: "5 days" },
  { hours: 168, label: "7 days" },
  { hours: 360, label: "15 days" },
  { hours: 720, label: "30 days" },
];

// Call outcomes as the LTR service records them.
export const CALL_ONGOING = "ongoing";
export const CALL_ANSWERED = "answered";
export const CALL_MISSED = "missed";

export const CALL_OUTCOME_LABEL_MAP = {
  [CALL_ONGOING]: "Ongoing",
  [CALL_ANSWERED]: "Answered",
  [CALL_MISSED]: "Missed",
};

// Appointment status from api-appointment-service (models/constants.go),
// for the appointment card's status chip.
export const APPOINTMENT_STATUS_LABEL_MAP = {
  0: "Scheduled",
  1: "In progress",
  2: "Completed",
  3: "Cancelled",
  4: "Confirmed",
  5: "Scheduled",
  6: "Reschedule requested",
};
